import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet, apiDelete, apiPatch } from '../services/api';
import { fmtMin as _fmtMinUTC } from '../services/dates';
import LeadsPage, { LeadDrawer, DEFAULT_TEMPLATES } from './LeadsPage';
import ClientesPage, { ClientDrawer } from './ClientesPage';
import CalendarPage from './CalendarPage';
import AjustesPage from './AjustesPage';
import CalculadoraPage from './CalculadoraPage';
import SuperAdminPage from './SuperAdminPage';
import UsersPage from './UsersPage';
import EstadisticasPage from './EstadisticasPage';
import MetricasPage from './MetricasPage';
import QuotesPage from './QuotesPage';
import FacturacionPage from './FacturacionPage';
import FichajePage from './FichajePage';
import InstaladoresPage from './InstaladoresPage';
import TicketsPage from './TicketsPage';
import './Dashboard.css';

const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const ICONS = {
  dashboard: "M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z",
  leads:     "M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75",
  calendar:  "M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z",
  calc:      "M9 7H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1-4h-4v4h4V3z",
  financial: "M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6",
  settings:  "M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z",
  logout:    "M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9",
  ranking:   "M18 20V10M12 20V4M6 20v-6",
  fichaje:   "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
  webhook:   "M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71",
  users:     "M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 3a4 4 0 010 8M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75",
  export:    "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3",
  ticket:    "M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z",
};

// adminOnly: oculto para comercial/empresa-cliente
// comercialOk: visible también para comercial/empresa-cliente
const MODULE_NAV = [
  { key: 'dashboard',  label: 'Panel',            icon: 'dashboard', always: true },
  { key: 'leads',      label: 'CRM Leads',         icon: 'leads',     module: 'crm' },
  { key: 'clientes',   label: 'Clientes',          icon: 'users',     module: 'crm',          adminOnly: true },
  { key: 'calendario', label: 'Calendario',        icon: 'calendar',  module: 'crm' },
  { key: 'quotes',     label: 'Presupuestos',      icon: 'calc',      module: 'financiero',   notComercial: true },
  { key: 'invoices',   label: 'Facturación',       icon: 'financial', module: 'financiero',   notComercial: true },
  { key: 'calculadora',   label: 'Calculadora',    icon: 'calc',        module: 'calculadora' },
  { key: 'estadisticas',  label: 'Estadísticas',   icon: 'ranking',     module: 'estadisticas', notComercial: true },
  { key: 'metricas',      label: 'Métricas',       icon: 'ranking',     module: 'bot_voz',      adminOnly: true },
  { key: 'fichaje',       label: 'Fichaje',         icon: 'fichaje',     module: 'fichaje' },
  { key: 'instaladores',  label: 'Instaladores',    icon: 'users',       module: 'instaladores', adminOnly: true },
  { key: 'tickets',       label: 'Soporte',         icon: 'ticket',      adminOnly: true },
];

const ADMIN_NAV = [
  { key: 'usuarios', label: 'Usuarios', icon: 'users' },
  { key: 'ajustes',  label: 'Ajustes',  icon: 'settings' },
];

const PAGE_TITLES = {
  dashboard:  'Panel principal',
  leads:      'CRM Leads',
  clientes:   'Clientes',
  calendario: 'Calendario de tareas',
  quotes:     'Presupuestos aceptados',
  invoices:   'Facturación',
  calculadora: 'Calculadora de vallas',
  usuarios:    'Usuarios',
  ajustes:     'Ajustes',
  tickets:     'Soporte',
  superadmin:    'Exportación de datos',
  estadisticas:  'Estadísticas',
  metricas:      'Métricas — Calculadora web',
  fichaje:       'Control de Fichajes',
  instaladores:  'Instaladores — Visitas e Instalaciones',
};

function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

// Oscurece un color hex en `amount` puntos (negativo = más oscuro)
function shadeColor(hex, amount) {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (n >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((n >> 8) & 0xff) + amount));
  const b = Math.min(255, Math.max(0, (n & 0xff) + amount));
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

function Placeholder({ title, desc }) {
  return (
    <div className="module-placeholder">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
        <rect x="3" y="3" width="18" height="18" rx="3"/><path d="M12 8v8M8 12h8"/>
      </svg>
      <h3>{title}</h3>
      <p>{desc || 'Módulo en construcción'}</p>
    </div>
  );
}

const PRIORITY_COLORS = { alta: '#ef4444', media: '#f59e0b', baja: '#94a3b8' };

function PanelPage({ onNavigate }) {
  const { auth } = useContext(AuthContext);
  const [tasks,    setTasks]    = useState([]);
  const [loadingT, setLoadingT] = useState(true);
  const [stats, setStats] = useState(null);
  const [selTask, setSelTask]   = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const today = new Date();
    const p = new URLSearchParams({ month: today.getMonth()+1, year: today.getFullYear(), status: 'pendiente' });
    apiGet(`/tasks?${p}`, auth.token)
      .then(data => {
        // Ordenar: con fecha primero (más próxima), sin fecha al final
        const sorted = [...data].sort((a,b) => {
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(String(a.dueDate).replace(' ','T')) - new Date(String(b.dueDate).replace(' ','T'));
        });
        setTasks(sorted.slice(0, 7));
      })
      .catch(() => {})
      .finally(() => setLoadingT(false));
  }, [auth.token]);

  useEffect(() => {
    // Stats del mes actual — todos los recuadros usan el mismo filtro
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    const from = `${y}-${m}-01`;
    const to   = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
    apiGet(`/stats?from=${from}&to=${to}`, auth.token)
      .then(data => setStats(data))
      .catch(() => {});
  }, [auth.token]);

  const fmtDue = d => {
    if (!d) return null;
    // Forzar interpretación como hora local (el backend devuelve "YYYY-MM-DD HH:MM:SS" sin timezone)
    const dt   = new Date(String(d).replace(' ', 'T'));
    const now  = new Date();
    // Comparar solo por día (ignorar horas para el cálculo de días)
    const dtDay  = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.round((dtDay - nowDay) / 86400000);
    if (diffDays < 0) return { label: 'Vencida', cls: 'panel-task-due--late' };
    if (diffDays === 0) return { label: 'Hoy ' + dt.toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit'}), cls: 'panel-task-due--today' };
    if (diffDays === 1) return { label: 'Mañana', cls: 'panel-task-due--soon' };
    return { label: dt.toLocaleDateString('es-ES',{day:'2-digit',month:'2-digit'}), cls: '' };
  };

  const getStatusCount = (status) => {
    if (!stats?.byStatus) return '—';
    const found = stats.byStatus.find(s => s.status === status);
    return found ? Number(found.count) : 0;
  };

  const fmtEuros = (val) => {
    const n = Number(val) || 0;
    return n.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
  };

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Leads este mes</div>
          <div className="stat-value blue">{stats ? stats.summary.total : '—'}</div>
          <div className="stat-sub">Nuevos leads captados</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Contactados</div>
          <div className="stat-value purple">{getStatusCount('contactado')}</div>
          <div className="stat-sub">WhatsApp enviado</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">En seguimiento</div>
          <div className="stat-value orange">{getStatusCount('seguimiento')}</div>
          <div className="stat-sub">Con presupuesto</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Firmados</div>
          <div className="stat-value green">{stats ? stats.summary.ganado : '—'}</div>
          <div className="stat-sub">Cerrados este mes</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Facturación</div>
          <div className="stat-value">{stats ? fmtEuros(stats.invoices.total_amount) : '—'}</div>
          <div className="stat-sub">Total con IVA</div>
        </div>
      </div>

      {/* Widget tareas próximas */}
      <div className="card panel-tasks-card">
        <div className="panel-tasks-header">
          <div className="card-title">📋 Mis tareas pendientes</div>
        </div>
        {loadingT ? (
          <div className="panel-tasks-empty">Cargando…</div>
        ) : tasks.length === 0 ? (
          <div className="panel-tasks-empty">
            No tienes tareas pendientes este mes.
          </div>
        ) : (
          <div className="panel-tasks-list">
            {tasks.map(t => {
              const due = fmtDue(t.dueDate);
              return (
                <div key={t.id} className="panel-task panel-task--clickable" onClick={() => setSelTask(t)}>
                  <span className="panel-task__dot" style={{background: PRIORITY_COLORS[t.priority]||'#94a3b8'}}/>
                  <div className="panel-task__body">
                    <span className="panel-task__title">{t.title}</span>
                    {(t.leadName||t.clientName) && (
                      <span className="panel-task__sub">{t.leadName||t.clientName}</span>
                    )}
                  </div>
                  {due && <span className={`panel-task__due ${due.cls}`}>{due.label}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal detalle de tarea */}
      {selTask && (
        <div className="panel-task-modal-overlay" onClick={() => setSelTask(null)}>
          <div className="panel-task-modal" onClick={e => e.stopPropagation()}>
            <div className="panel-task-modal__header">
              <span className="panel-task__dot" style={{background: PRIORITY_COLORS[selTask.priority]||'#94a3b8', width:10, height:10}}/>
              <h3 className="panel-task-modal__title">{selTask.title}</h3>
              <button className="panel-task-modal__close" onClick={() => setSelTask(null)}>&times;</button>
            </div>
            <div className="panel-task-modal__body">
              {selTask.description && (
                <div className="panel-task-modal__field">
                  <label>Descripción</label>
                  <p>{selTask.description}</p>
                </div>
              )}
              <div className="panel-task-modal__row">
                {selTask.dueDate && (
                  <div className="panel-task-modal__field">
                    <label>Fecha límite</label>
                    <p>{new Date(selTask.dueDate).toLocaleDateString('es-ES', { day:'2-digit', month:'long', year:'numeric' })}</p>
                  </div>
                )}
                <div className="panel-task-modal__field">
                  <label>Prioridad</label>
                  <p style={{textTransform:'capitalize'}}>{selTask.priority || 'Media'}</p>
                </div>
                <div className="panel-task-modal__field">
                  <label>Estado</label>
                  <p style={{textTransform:'capitalize'}}>{selTask.status || 'Pendiente'}</p>
                </div>
              </div>
              {(selTask.leadName || selTask.clientName) && (
                <div className="panel-task-modal__field">
                  <label>Asociada a</label>
                  <p>{selTask.leadName || selTask.clientName}</p>
                </div>
              )}
              {selTask.createdByName && (
                <div className="panel-task-modal__field">
                  <label>Creada por</label>
                  <p>{selTask.createdByName}</p>
                </div>
              )}
            </div>
            <div className="panel-task-modal__footer">
              <button
                className="panel-task-modal__delete"
                disabled={deleting}
                onClick={async () => {
                  if (!window.confirm('¿Eliminar esta tarea?')) return;
                  setDeleting(true);
                  try {
                    await apiDelete(`/tasks/${selTask.id}`, auth.token);
                    setTasks(prev => prev.filter(t => t.id !== selTask.id));
                    setSelTask(null);
                  } catch (e) { alert(e.message); }
                  finally { setDeleting(false); }
                }}
              >
                {deleting ? 'Eliminando…' : 'Eliminar tarea'}
              </button>
              <button className="panel-task-modal__close-btn" onClick={() => setSelTask(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── ReminderPopup — polling de recordatorios ─────────────────────────────────

const REMINDER_LABELS = { '1d':'1 día antes', '1h':'1 hora antes', '30m':'30 min antes', '15m':'15 min antes', '0m':'Ahora' };

function ReminderPopup({ token }) {
  const [alerts, setAlerts] = useState([]);
  const dismissed = useRef(new Set());

  const check = useCallback(async () => {
    try {
      const reminders = await apiGet('/tasks/reminders', token);
      const fresh = reminders.filter(t => !dismissed.current.has(t.id));
      if (fresh.length) setAlerts(prev => {
        const ids = new Set(prev.map(a => a.id));
        return [...prev, ...fresh.filter(t => !ids.has(t.id))];
      });
    } catch {}
  }, [token]);

  useEffect(() => {
    check();
    const iv = setInterval(check, 60 * 1000); // cada 60s
    return () => clearInterval(iv);
  }, [check]);

  async function dismiss(id) {
    dismissed.current.add(id);
    setAlerts(prev => prev.filter(a => a.id !== id));
    try { await apiPatch(`/tasks/${id}/reminder-sent`, {}, token); } catch {}
  }

  if (!alerts.length) return null;

  return (
    <div style={{ position:'fixed', top:16, right:16, zIndex:9999, display:'flex', flexDirection:'column', gap:10, maxWidth:380 }}>
      {alerts.map(t => (
        <div key={t.id} style={{ background:'#fff', border:'1px solid #e2e8f0', borderLeft:'4px solid #E84300', borderRadius:10, padding:'14px 18px', boxShadow:'0 8px 30px rgba(0,0,0,.15)', animation:'slideIn .3s ease' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
            <div>
              <div style={{ fontSize:'.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', color:'#E84300', marginBottom:4 }}>
                Recordatorio · {REMINDER_LABELS[t.reminder] || t.reminder}
              </div>
              <div style={{ fontSize:'.92rem', fontWeight:700, color:'#1a1a2e' }}>{t.title}</div>
              {t.dueDate && <div style={{ fontSize:'.78rem', color:'#64748b', marginTop:2 }}>
                {new Date(String(t.dueDate).replace(' ','T')).toLocaleString('es-ES',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}
              </div>}
              {(t.leadName||t.clientName) && <div style={{ fontSize:'.75rem', color:'#94a3b8', marginTop:2 }}>{t.leadName||t.clientName}</div>}
            </div>
            <button onClick={()=>dismiss(t.id)} style={{ background:'none', border:'none', fontSize:'1.2rem', color:'#94a3b8', cursor:'pointer', padding:0, lineHeight:1 }}>✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { auth, setAuth } = useContext(AuthContext);
  const [page, setPage]       = useState('dashboard');
  const [calcLead, setCalcLead] = useState(null);
  const [overlayLeadId,   setOverlayLeadId]   = useState(null);
  const [overlayClientId, setOverlayClientId] = useState(null);

  function handleOpenLead(id)   { if (id) setOverlayLeadId(id);   }
  function handleOpenClient(id) { if (id) setOverlayClientId(id); }

  useEffect(() => { setOverlayLeadId(null); setOverlayClientId(null); }, [page]);

  // Refresco de sesión al montar: módulos, tenant y sub_tenant actualizados
  useEffect(() => {
    apiGet('/auth/me', auth.token)
      .then(fresh => {
        setAuth(prev => prev && ({ ...prev, ...fresh }));
      })
      .catch(err => {
        // Token inválido o usuario/tenant inactivo → forzar logout
        if (/401|inactivo|inválido/i.test(err.message)) setAuth(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // empresa-cliente usa sus propios colores de sub_tenant; el resto usa los del tenant
    const colors = (auth.user?.role === 'empresa-cliente' && auth.sub_tenant?.colors)
      ? auth.sub_tenant.colors
      : (auth.tenant?.colors || {});
    const root = document.documentElement;
    if (colors.brand)      { root.style.setProperty('--brand',      colors.brand);
                             root.style.setProperty('--brand-dark', shadeColor(colors.brand, -20)); }
    if (colors.brandLight) root.style.setProperty('--brand-light', colors.brandLight);
    if (colors.dark)       root.style.setProperty('--dark',        colors.dark);
    if (colors.pageBg)     root.style.setProperty('--page-bg',     colors.pageBg);
  }, [auth.tenant, auth.sub_tenant, auth.user?.role]);

  const modules = auth.modules || [];
  const role    = auth.user?.role;
  const isAdmin = ['admin', 'superadmin'].includes(role);
  const isComercialOrSub = role === 'comercial' || role === 'empresa-cliente';
  // Empresa-cliente usa su propio branding (sub_tenant); el resto usa el del tenant
  const baseTenant = auth.tenant || { name: 'Mi empresa', logo: null, colors: {} };
  const tenant = (role === 'empresa-cliente' && auth.sub_tenant)
    ? { ...baseTenant, name: auth.sub_tenant.name || baseTenant.name, logo: auth.sub_tenant.logo ?? baseTenant.logo, colors: auth.sub_tenant.colors || baseTenant.colors, company_data: auth.sub_tenant.company_data || baseTenant.company_data }
    : baseTenant;
  const { user } = auth;

  const isSuperadmin = role === 'superadmin';
  const visibleNav = MODULE_NAV.filter(n => {
    if (!n.always && !isSuperadmin && n.module && !modules.includes(n.module)) return false;
    if (n.adminOnly    && !isAdmin)                                    return false;
    if (n.notComercial && role === 'comercial')                        return false;
    return true;
  });
  const hasCalc = isSuperadmin || modules.includes('calculadora');

  function handleGenerarPresupuesto(lead) {
    // Pasamos el lead_id REAL: al guardar, la proforma se asocia a ESTE lead y no se
    // re-busca por teléfono (evita adjuntarla a un lead duplicado con el mismo número).
    setCalcLead({
      nombre: lead.name || '', poblacion: lead.city || '', tel: lead.phone || '', email: lead.email || '',
      lead_id: lead.id, leadCode: lead.leadCode || '',
    });
    setPage('calculadora');
  }

  // Generar presupuesto desde un cliente: la proforma se asociará al client_id (no a un lead)
  function handleGenerarPresupuestoCliente(client) {
    setCalcLead({
      nombre:    client.name || client.company_name || '',
      poblacion: client.city || '',
      tel:       client.phone || '',
      email:     client.email || '',
      client_id: client.id,
    });
    setPage('calculadora');
  }

  function renderPage() {
    switch (page) {
      case 'dashboard': return <PanelPage onNavigate={setPage}/>;
      case 'leads':      return <LeadsPage onGenerarPresupuesto={hasCalc ? handleGenerarPresupuesto : null}/>;
      case 'clientes':   return <ClientesPage onGenerarPresupuesto={hasCalc ? handleGenerarPresupuestoCliente : null} />;
      case 'calendario': return <CalendarPage onOpenLead={handleOpenLead} onOpenClient={handleOpenClient}/>;
      case 'quotes':    return <QuotesPage />;
      case 'invoices':  return <FacturacionPage />;
      case 'calculadora': return <CalculadoraPage initialCliente={calcLead} />;
      case 'usuarios':     return <UsersPage />;
      case 'tickets':      return <TicketsPage />;
      case 'ajustes':      return <AjustesPage />;
      case 'superadmin':   return <SuperAdminPage />;
      case 'estadisticas': return <EstadisticasPage />;
      case 'metricas':     return <MetricasPage />;
      case 'fichaje':      return <FichajePage />;
      case 'instaladores': return <InstaladoresPage />;
      default:             return null;
    }
  }

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          {tenant.logo
            ? <img src={tenant.logo} alt={tenant.name} className="sidebar-tenant-logo" />
            : <div className="sidebar-brand">
                <span className="sidebar-brand-name">
                  <span className="sidebar-brand-g">g</span>estavin
                </span>
              </div>
          }
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">Principal</div>
          {visibleNav.map(n => (
            <button key={n.key} className={`nav-item${page === n.key ? ' active' : ''}`} onClick={() => setPage(n.key)}>
              <Icon d={ICONS[n.icon]} size={16} />
              {n.label}
            </button>
          ))}
          {isAdmin && (
            <>
              <div className="nav-section">Administración</div>
              {ADMIN_NAV.map(n => (
                <button key={n.key} className={`nav-item${page === n.key ? ' active' : ''}`} onClick={() => setPage(n.key)}>
                  <Icon d={ICONS[n.icon]} size={16} />
                  {n.label}
                </button>
              ))}
            </>
          )}
          {isComercialOrSub && (
            <>
              <div className="nav-section">Mi cuenta</div>
              <button className={`nav-item${page === 'ajustes' ? ' active' : ''}`} onClick={() => setPage('ajustes')}>
                <Icon d={ICONS.settings} size={16} />
                Ajustes
              </button>
            </>
          )}
          {auth.user?.role === 'superadmin' && (
            <>
              <div className="nav-section">Sistema</div>
              <button className={`nav-item${page === 'superadmin' ? ' active' : ''}`} onClick={() => setPage('superadmin')}>
                <Icon d={ICONS.export} size={16} />
                Exportación
              </button>
            </>
          )}
        </nav>

        <div className="sidebar-bottom">
          <div className="user-card">
            <div className="user-avatar">{initials(user.name)}</div>
            <div className="user-info">
              <div className="user-name">{user.name}</div>
              <div className="user-role">{user.role}</div>
            </div>
            <button className="logout-btn" onClick={() => setAuth(null)} title="Cerrar sesión">
              <Icon d={ICONS.logout} size={15} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <div className="topbar">
          <div className="topbar-title">{PAGE_TITLES[page]}</div>
          <div style={{display:'flex',alignItems:'center',gap:'.8rem'}}>
            {isAdmin && <ApprovalsBell token={auth.token} onLeadOpen={handleOpenLead} />}
            <span className="topbar-badge">{tenant.name}</span>
          </div>
        </div>
        <div className="page-body">
          {renderPage()}
        </div>
      </main>

      <ReminderPopup token={auth.token} />

      {overlayLeadId && (
        <OverlayLeadDrawer leadId={overlayLeadId} token={auth.token} admin={isAdmin}
          onClose={() => setOverlayLeadId(null)}
          onGenerarPresupuesto={hasCalc ? handleGenerarPresupuesto : null} />
      )}
      {overlayClientId && (
        <OverlayClientDrawer clientId={overlayClientId} token={auth.token}
          onClose={() => setOverlayClientId(null)}
          onGenerarPresupuesto={hasCalc ? handleGenerarPresupuestoCliente : null} />
      )}
    </div>
  );
}

function OverlayLeadDrawer({ leadId, token, admin, onClose, onGenerarPresupuesto }) {
  const [lead, setLead] = useState(null);
  const [comerciales, setComerciales] = useState([]);
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);

  const refetch = useCallback(() => {
    apiGet(`/leads/${leadId}`, token).then(setLead).catch(() => {});
  }, [leadId, token]);

  useEffect(() => { refetch(); }, [refetch]);

  useEffect(() => {
    const url = admin ? '/settings' : '/users/me/templates';
    apiGet(url, token).then(d => {
      const saved = admin ? (d.contact_templates || {}) : d;
      const merged = { ...DEFAULT_TEMPLATES };
      Object.entries(saved || {}).forEach(([k, v]) => { if (v) merged[k] = v; });
      setTemplates(merged);
    }).catch(() => {});
  }, [admin, token]);

  useEffect(() => {
    if (admin) apiGet('/users/comerciales', token).then(setComerciales).catch(() => {});
  }, [admin, token]);

  if (!lead) return null;
  return (
    <LeadDrawer lead={lead} token={token} comerciales={comerciales} templates={templates}
      onClose={onClose} onUpdated={refetch} onGenerarPresupuesto={onGenerarPresupuesto} />
  );
}

function OverlayClientDrawer({ clientId, token, onClose, onGenerarPresupuesto }) {
  return <ClientDrawer clientId={clientId} token={token} onClose={onClose} onUpdated={() => {}}
    onGenerarPresupuesto={onGenerarPresupuesto} />;
}

// ── ApprovalsBell — campana con badge de solicitudes pendientes ────────────────
function ApprovalsBell({ token, onLeadOpen }) {
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [reviewing, setReviewing] = useState(null); // approval object

  const load = useCallback(async () => {
    try { setPending(await apiGet('/lead-approvals/pending', token)); }
    catch {}
  }, [token]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30 * 1000);
    return () => clearInterval(iv);
  }, [load]);

  const count = pending.length;

  return (
    <>
      <button
        onClick={() => setOpen(p => !p)}
        style={{position:'relative',background:'none',border:'1px solid var(--border)',borderRadius:8,padding:'.4rem .6rem',cursor:'pointer',display:'flex',alignItems:'center',gap:6,color:'var(--text)'}}
        title={count > 0 ? `${count} pago(s) pendientes de aprobar` : 'Sin aprobaciones pendientes'}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {count > 0 && (
          <span style={{position:'absolute',top:-6,right:-6,background:'#dc2626',color:'#fff',fontSize:10,fontWeight:700,borderRadius:10,padding:'2px 6px',minWidth:18,textAlign:'center'}}>
            {count}
          </span>
        )}
      </button>

      {open && (
        <div style={{position:'fixed',top:60,right:24,zIndex:1500,background:'#fff',border:'1px solid var(--border)',borderRadius:12,boxShadow:'0 12px 40px rgba(0,0,0,.15)',width:380,maxHeight:'70vh',overflow:'auto'}} onClick={e => e.stopPropagation()}>
          <div style={{padding:'14px 18px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <strong style={{fontSize:14}}>Pagos pendientes de revisar</strong>
            <button onClick={() => setOpen(false)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18,color:'#888'}}>✕</button>
          </div>
          {count === 0 ? (
            <div style={{padding:30,textAlign:'center',color:'#888',fontSize:13}}>No hay solicitudes pendientes.</div>
          ) : (
            <div>
              {pending.map(a => (
                <div key={a.id}
                  style={{padding:'12px 18px',borderBottom:'1px solid var(--border)',cursor:'pointer'}}
                  onClick={() => { setReviewing(a); setOpen(false); }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{fontWeight:600,fontSize:14}}>{a.leadName} <span style={{color:'#9ca3af',fontSize:11,fontWeight:400}}>· {a.leadCode}</span></div>
                  <div style={{fontSize:12,color:'#6b7280',marginTop:2}}>{a.requestedByName} · {Number(a.budgetTotal).toLocaleString('es-ES',{minimumFractionDigits:2})} € · {a.firstPaymentPct}%</div>
                  <div style={{fontSize:11,color:'#9ca3af',marginTop:2}}>{_fmtMinUTC(a.requestedAt)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {reviewing && (
        <ApprovalReviewModal approval={reviewing} token={token}
          onClose={() => setReviewing(null)}
          onLeadOpen={onLeadOpen}
          onDone={() => { setReviewing(null); load(); }} />
      )}
    </>
  );
}

function ApprovalReviewModal({ approval, token, onClose, onDone, onLeadOpen }) {
  const [proformaHtml, setProformaHtml] = useState('');
  const [loadingPrf, setLoadingPrf] = useState(true);
  const [mode, setMode] = useState(null); // null | 'reject'
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!approval.proformaId) { setLoadingPrf(false); return; }
    apiGet(`/proformas/${approval.proformaId}`, token)
      .then(p => setProformaHtml(p.html || ''))
      .catch(() => {})
      .finally(() => setLoadingPrf(false));
  }, [approval.proformaId, token]);

  async function decide(action) {
    setBusy(true);
    try {
      const body = action === 'approve' ? { action: 'approve' } : { action: 'reject', reject_reason: rejectReason };
      const r = await apiPatch(`/lead-approvals/${approval.id}`, body, token);
      if (action === 'approve') {
        alert(r.emailSent
          ? '✅ Aprobado y email enviado a fábrica'
          : `⚠️ Aprobado pero el email a fábrica falló: ${r.emailError || 'desconocido'}`);
      }
      onDone();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }

  const tot  = Number(approval.budgetTotal) || 0;
  const res  = Number(approval.reservationAmount) || 0;
  const fp   = Number(approval.firstPaymentAmount) || 0;
  const rest = Number(approval.remainingAmount) || 0;

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:1600,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}>
      <div style={{background:'#fff',borderRadius:14,width:'100%',maxWidth:720,maxHeight:'90vh',overflow:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.3)'}} onClick={e => e.stopPropagation()}>
        <div style={{padding:'18px 24px',borderBottom:'1px solid #e5e7eb',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <h3 style={{margin:0,fontSize:16}}>Revisar primer pago — {approval.leadName}</h3>
            <div style={{fontSize:12,color:'#6b7280',marginTop:2}}>{approval.leadCode} · solicitado por {approval.requestedByName}</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',fontSize:22,color:'#888'}}>✕</button>
        </div>
        <div style={{padding:24}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:18}}>
            <div style={{background:'#f3f4f6',padding:'12px 14px',borderRadius:8}}>
              <div style={{fontSize:11,textTransform:'uppercase',letterSpacing:'.06em',color:'#6b7280'}}>Cliente</div>
              <div style={{fontSize:14,fontWeight:600,marginTop:2}}>{approval.leadName}</div>
              {approval.leadPhone && <div style={{fontSize:12,color:'#6b7280'}}>Tel: {approval.leadPhone}</div>}
              {approval.leadCity && <div style={{fontSize:12,color:'#6b7280'}}>{approval.leadCity}</div>}
            </div>
            <div style={{background:'#f3f4f6',padding:'12px 14px',borderRadius:8}}>
              <div style={{fontSize:11,textTransform:'uppercase',letterSpacing:'.06em',color:'#6b7280'}}>Comercial</div>
              <div style={{fontSize:14,fontWeight:600,marginTop:2}}>{approval.requestedByName}</div>
              <div style={{fontSize:12,color:'#6b7280'}}>Asignado: {approval.assignedName || '—'}</div>
            </div>
          </div>

          <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:10,padding:'16px 18px',marginBottom:18}}>
            <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:14}}>
              <span>Total presupuesto</span>
              <strong>{tot.toLocaleString('es-ES',{minimumFractionDigits:2})} €</strong>
            </div>
            {res > 0 && (
              <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:14,color:'#16a34a'}}>
                <span>Reserva entregada</span>
                <strong>−{res.toLocaleString('es-ES',{minimumFractionDigits:2})} €</strong>
              </div>
            )}
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0 4px',marginTop:4,borderTop:'2px solid #ddd',fontSize:15,fontWeight:700,color:'#166534'}}>
              <span>Primer pago ({approval.firstPaymentPct}%)</span>
              <span>{fp.toLocaleString('es-ES',{minimumFractionDigits:2})} €</span>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:13,color:'#374151'}}>
              <span>Restante (día instalación)</span>
              <strong>{rest.toLocaleString('es-ES',{minimumFractionDigits:2})} €</strong>
            </div>
          </div>

          <div style={{marginBottom:18}}>
            <div style={{fontSize:12,textTransform:'uppercase',letterSpacing:'.06em',color:'#6b7280',marginBottom:8}}>Presupuesto completo</div>
            {loadingPrf ? (
              <div style={{padding:30,textAlign:'center',color:'#888',fontSize:13}}>Cargando…</div>
            ) : proformaHtml ? (
              <iframe title="proforma" srcDoc={proformaHtml} style={{width:'100%',height:380,border:'1px solid #e5e7eb',borderRadius:8,background:'#fff'}}/>
            ) : (
              <div style={{padding:20,textAlign:'center',color:'#9ca3af',fontSize:13,background:'#f9fafb',borderRadius:8}}>
                Sin presupuesto vinculado a esta solicitud.
                {approval.leadId && onLeadOpen && (
                  <div style={{marginTop:10}}>
                    <button onClick={() => { onLeadOpen(approval.leadId); onClose(); }} style={{padding:'6px 12px',fontSize:12,background:'#1f2937',color:'#fff',border:'none',borderRadius:6,cursor:'pointer'}}>
                      Abrir lead completo
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {mode === 'reject' && (
            <div style={{marginBottom:18}}>
              <label style={{display:'block',fontSize:12,fontWeight:600,marginBottom:6,color:'#374151'}}>Motivo del rechazo *</label>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                rows={3} placeholder="Explica qué hay que corregir…"
                style={{width:'100%',padding:'8px 10px',border:'1px solid #d1d5db',borderRadius:6,fontFamily:'inherit',fontSize:13,resize:'vertical'}}/>
            </div>
          )}
        </div>
        <div style={{padding:'14px 24px',borderTop:'1px solid #e5e7eb',display:'flex',gap:8,justifyContent:'flex-end',background:'#f9fafb'}}>
          {mode === 'reject' ? (
            <>
              <button onClick={() => { setMode(null); setRejectReason(''); }} disabled={busy}
                style={{padding:'8px 14px',background:'#fff',border:'1px solid #d1d5db',borderRadius:6,cursor:'pointer',fontSize:13}}>
                Volver
              </button>
              <button onClick={() => decide('reject')} disabled={busy || rejectReason.trim().length < 5}
                style={{padding:'8px 14px',background:'#dc2626',color:'#fff',border:'none',borderRadius:6,cursor:'pointer',fontSize:13,fontWeight:600,opacity:(busy || rejectReason.trim().length < 5)?.5:1}}>
                {busy ? 'Enviando…' : 'Confirmar rechazo'}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setMode('reject')} disabled={busy}
                style={{padding:'8px 14px',background:'#fff',border:'1px solid #dc2626',color:'#dc2626',borderRadius:6,cursor:'pointer',fontSize:13,fontWeight:600}}>
                ✕ Incorrecto
              </button>
              <button onClick={() => decide('approve')} disabled={busy}
                style={{padding:'8px 14px',background:'#16a34a',color:'#fff',border:'none',borderRadius:6,cursor:'pointer',fontSize:13,fontWeight:600,opacity:busy?.5:1}}>
                {busy ? 'Aprobando…' : '✓ Correcto · Enviar a fábrica'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
